import { createAsyncThunk } from '@reduxjs/toolkit';

import { client } from 'src/graphql/client';
import { REQUOTE_PROVIDER_ROUTE } from 'src/graphql/requests/requoteProviderRoute';
import { GRequoteProviderRouteMutation } from 'src/graphql/types/generated';
import { logWarning } from 'src/services/datadog';
import type { AppDispatch } from 'src/state/store';
import { firstUserErrorMessage } from 'src/utils';
import type { VehicleChoice } from 'src/utils/thirdPartyDelivery';
import { ProviderRouteResult } from './markRouteForProvider';
import { refetchRouteList } from './refetchRouteList';

type RequoteProviderRouteArgs = {
  routeId: string;
  // ISO8601 in UTC, or null for ASAP. Always sent: LDS overwrites the
  // dispatch's pickup time with whatever a requote carries, absent included.
  pickupAt: string | null;
  // Sent only when the GM chose one in Change; left out, LDS keeps the class
  // the route was last quoted for, so Request quotes again never resets it.
  // Null asks for any class.
  vehicleType?: VehicleChoice;
  warehouseId: string;
  nightEnd: string;
};

export const requoteProviderRoute = createAsyncThunk(
  'delivery/requoteProviderRoute',
  async (
    {
      routeId,
      pickupAt,
      vehicleType,
      warehouseId,
      nightEnd,
    }: RequoteProviderRouteArgs,
    thunkAPI,
  ): Promise<ProviderRouteResult> => {
    const { data, error } = await client
      .mutation<GRequoteProviderRouteMutation>(REQUOTE_PROVIDER_ROUTE, {
        input: {
          routeId,
          pickupAt,
          ...(vehicleType !== undefined ? { vehicleType } : {}),
        },
      })
      .toPromise();

    refetchRouteList(thunkAPI.dispatch as AppDispatch, {
      warehouseId,
      nightEnd,
    });

    const payload = data?.requoteProviderRoute;
    const userErrors = payload?.userErrors ?? [];

    if (userErrors.length > 0) {
      logWarning('third_party.requote_route_failed', {
        routeId,
        userError: userErrors[0]?.__typename,
        transportError: false,
      });
      return { ok: false, message: firstUserErrorMessage(userErrors) };
    }

    // The dialog shows the failure in place; its poll shows whether the
    // requote landed.
    if (error || !payload) {
      logWarning('third_party.requote_route_failed', {
        routeId,
        transportError: Boolean(error),
      });
      return { ok: false };
    }

    return { ok: true };
  },
);
